import React from 'react';
import { motion } from 'framer-motion';

const TeamSection = () => {
  const team = [
    {
      name: 'Majd Zaben',
      role: 'Residential Leasing Expert',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776810526/Majd_Zaben_p8egux.png',
      bio: `5 years in mastering Dubai's rental landscape, building a reputation for honest advice and smooth transactions.`,
    },
    {
      name: 'Ali Naqvi',
      role: 'Commercial & Investment Advisor',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776810526/aLI_NAQVI_lmihif.png',
      bio: `6 years of experience in Dubai's commercial real estate sector, Ali specialises in helping businesses and investors make confident decisions.`,
    },
    {
      name: 'Moulik Rao',
      role: 'Luxury & Off-Plan Specialist',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776810527/Moulik_Rao_w4cien.png',
      bio: `4 years of focused expertise in Dubai's luxury and off-plan property sector, helping clients identify high-value opportunities.`,
    },
    {
      name: 'Ahaskar Pandey',
      role: 'Senior Property Consultant & Founder',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1776810527/Ahaskar_pandey_jcmwwn.png',
      bio: `7+ years of deep-rooted experience in Dubai's real estate market and one of the most trusted names in off-plan investments.`,
    },
  ];

  return (
    <section id="team" className="relative py-24 overflow-hidden bg-white">
      {/* Background effects */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-1/3 left-1/4 w-96 h-96 bg-yellow-50 rounded-full blur-3xl opacity-30"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="text-gray-800">Meet The</span>
            <br />
            <span className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent">
              Wealth Creators
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Expert team with deep market knowledge and commitment to your success
          </p>
        </motion.div>

        {/* Team grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {team.map((member, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 80, rotateY: -30 }}
              whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{
                delay: index * 0.15,
                duration: 0.8,
                type: "spring",
                stiffness: 100
              }}
            >
              <motion.div
                className="relative overflow-hidden rounded-3xl cursor-pointer"
                whileHover={{ y: -10 }}
                transition={{ duration: 0.4 }}
              >
                {/* Card container */}
                <div className="relative h-[550px] bg-white border border-gray-200 overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500">
                  {/* Image section */}
                  <div className="relative h-[350px] overflow-hidden">
                    <motion.img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      whileHover={{ scale: 1.1 }}
                    />
                    
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />

                    {/* Decorative corner element */}
                    <motion.div
                      className="absolute bottom-0 left-0 w-24 h-24 border-l-4 border-b-4 border-yellow-400/40"
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.15 + 0.5 }}
                    />
                  </div>

                  {/* Content section */}
                  <div className="p-6 space-y-3">
                    {/* Name */}
                    <motion.h3
                      className="text-2xl font-bold text-gray-800"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.15 + 0.3 }}
                    >
                      {member.name}
                    </motion.h3>

                    {/* Role */}
                    <motion.div
                      className="text-yellow-600 font-semibold"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.15 + 0.4 }}
                    >
                      {member.role}
                    </motion.div>

                    {/* Bio */}
                    <motion.p
                      className="text-gray-600 text-sm leading-relaxed"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.15 + 0.5 }}
                    >
                      {member.bio}
                    </motion.p>
                  </div>

                  {/* Animated border on hover */}
                  <motion.div
                    className="absolute inset-0 border-2 border-yellow-400/0 group-hover:border-yellow-400/60 rounded-3xl transition-all duration-500 pointer-events-none"
                  />

                  {/* Glow effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-t from-yellow-400/0 to-yellow-400/0 group-hover:from-yellow-400/10 group-hover:to-transparent transition-all duration-500 pointer-events-none"
                  />

                  {/* Floating particles on hover */}
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-2 h-2 bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100"
                      style={{
                        top: `${30 + i * 20}%`,
                        left: `${20 + i * 25}%`,
                      }}
                      animate={{
                        y: [0, -30, 0],
                        opacity: [0, 1, 0],
                      }}
                      transition={{
                        duration: 2 + i * 0.5,
                        repeat: Infinity,
                        delay: i * 0.3,
                      }}
                    />
                  ))}
                </div>
              </motion.div>

              {/* Reflection effect under card */}
              <motion.div
                className="absolute -bottom-2 left-4 right-4 h-16 bg-gradient-to-b from-yellow-400/10 to-transparent blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"
              />
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="mt-20 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
        >
          <p className="text-gray-600 text-lg mb-6">
            Ready to work with Dubai's finest real estate experts?
          </p>
          <motion.a
            href="#contact"
            className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900 font-bold text-lg rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.98 }}
          >
            <span>Schedule a Consultation</span>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default TeamSection;
